import PropTypes from 'prop-types'
import qs from 'query-string'
import { useCallback, useContext, useMemo, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'

import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'
import { useWindowWidth } from 'core/ui/hooks/useWindowWidth'

import LoadingV2 from 'legacy/components/LoadingV2/LoadingV2'
import { LoadBoardContext, LOAD_BOARD_ACTIONS } from 'legacy/contexts/loadBoard'
import { getLoadOfferType } from 'legacy/utils/getLoadOfferType'

import { useConfigContext } from 'config/ui/ConfigContext'
import { shouldDisableBid, shouldShowBidCountdown } from 'loads/domain/Bid'
import { getLoadBookingType } from 'loads/domain/LoadOffer'
import { useBookingOffer } from 'loads/ui/hooks/useBookingOffer'
import { useCountdown } from 'loads/ui/hooks/useCountdown'

import { useAwardedBid } from '../../../../loads/ui/hooks/useAwardedBid'
import { useAnalytics } from '../hooks/useAnalytics'
import { getButtonLabel } from '../presenters/awardedBidPresenter'
import { RejectLoadModal } from '../RejectLoadModal/RejectLoadModal'
import { getRequirementsFromLoad } from '../utils/getRequirementsFromLoad'
import { getLoadFeatures } from '../utils/loadUtils'
import { OfferCardDesktop } from './OfferCardDesktop'
import { OfferCardMobile } from './OfferCardMobile'

export const CONTEXTS = {
  LOAD_BOARD: 'Load Board',
  BOOK_DIALOG_RELATED_LOAD: 'Book Dialog Related Load',
  TOP_OFFERS: 'Top Offers',
  BID_DIALOG: 'Bid Dialog',
  PENDING_RESPONSES: 'Pending Responses',
  RELATED_LOAD: 'Related Load',
  PUBLIC_RELATED_LOAD: 'Public Related Load',
  LOAD_SETUP_RELATED_LOAD: 'Load setup related load',
}

const Wrapper = styled.div`
  width: 100%;
  height: auto;
`

const UnstyledLink = styled(Link)`
  color: ${getToken('color-neutral-dark')};

  cursor: pointer;
`

const UnstyledAnchor = styled.a`
  color: ${getToken('color-neutral-dark')};
  text-decoration: none;
`

const DISMISS_BID_TOOLTIP_KEY = 'loadSmart.bid-dismissToolTip'

function OfferCard(props) {
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const { pathname: locationPathname, search: locationSearch } = useLocation()
  const history = useHistory()
  const { bookingOfferId, bookingStep, ...query } = useMemo(
    () => qs.parse(locationSearch),
    [locationSearch]
  )

  const { loadOffer, onCardRedirect, context, showPTL } = props
  const { uuid: loadUUID } = loadOffer.load
  const offerType = useMemo(() => getLoadOfferType(loadOffer), [loadOffer])
  const loadFeatures = useMemo(() => getLoadFeatures(loadOffer), [loadOffer])

  const windowWidth = useWindowWidth()

  const isPublicRelatedLoad = context === CONTEXTS.PUBLIC_RELATED_LOAD
  const isBookDialogRelatedLoad = context === CONTEXTS.BOOK_DIALOG_RELATED_LOAD

  const [{ carrier, bookingOffer, bids }, dispatch] = useContext(LoadBoardContext)
  const { ready: isCarrierReady } = carrier || {}
  const isBooking = (bookingOffer && bookingOffer.id === loadUUID) || false
  const { navigateToBookOffer } = useBookingOffer()

  const hasActiveDNU = carrier?.has_active_dnu ?? false
  const activeBid = bids[loadUUID]

  const nextBidTime = loadOffer?.bid_allowed?.next_bid_time
  const { countdown, countdownHasFinished } = useCountdown(nextBidTime)

  const showBidCountdown = shouldShowBidCountdown({
    carrier,
    countdownHasFinished,
    nextBidTime,
  })
  const isBidDisabled = shouldDisableBid({
    carrier,
    activeBid,
    showBidCountdown,
  })

  const { analytics } = useAnalytics()
  const showOnboardingImprovements = useConfigContext().showOnboardingImprovements.toBoolean()

  const storage = useMemo(() => new LocalStorageClient(), [])
  const displayTooltip = !Boolean(storage.get(DISMISS_BID_TOOLTIP_KEY))

  const requirements = getRequirementsFromLoad(loadOffer.load)
  /**
   * Creating new const to be used by RejectLoadModal
   * Set `active_offer` because it's a prop needed on RejectLoadModal
   * OfferCard uses a structure that the properties for `active_offer` are in the root object structure
   */
  const load = {
    ...loadOffer.load,
    active_offer: {
      uuid: loadOffer.uuid,
      contract: loadOffer.contract,
    },
  }

  const RMISFinishOnboardingUrl = `${locationPathname}?finishOnboarding=1`

  const urlToLoad = useMemo(() => {
    const queryString = Object.keys(query).length ? `?${qs.stringify(query)}` : ''
    const defaultUrl = `${loadUUID}${queryString}`
    return `/loads/${defaultUrl}`
  }, [loadUUID, query])

  const tagCardClickAnalytics = useCallback(() => {
    analytics.track(['Load Offer', 'See Details'], {
      triggerContext: context,
      offerType,
      loadUUID,
      features: loadFeatures,
    })
    analytics.tag(['Clicked Load Details'])
  }, [analytics, context, loadFeatures, loadUUID, offerType])

  const awardedBid = useAwardedBid(loadOffer.load)

  const handleBook = useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()

      if (showOnboardingImprovements && !isCarrierReady) {
        history.push(RMISFinishOnboardingUrl)
        return
      }

      const bookingType = getLoadBookingType(
        offerType,
        Boolean(awardedBid),
        Boolean(awardedBid?.counteredBid)
      )
      navigateToBookOffer({
        id: loadUUID,
        position: loadOffer.position,
        triggerContext: context,
        bookingType,
      })

      const trackingTrigger = awardedBid ? 'Accept Awarded Bid Button' : 'Book Button'
      analytics.track(['Load Offer', trackingTrigger], {
        position: loadOffer.position,
        triggerContext: context,
        offerType,
        loadUUID,
        loadMode: loadOffer.load.mode,
      })
    },
    [
      showOnboardingImprovements,
      isCarrierReady,
      navigateToBookOffer,
      loadUUID,
      loadOffer.position,
      loadOffer.load.mode,
      context,
      awardedBid,
      analytics,
      offerType,
      history,
      RMISFinishOnboardingUrl,
    ]
  )

  const handleBid = useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()

      query.bidding = loadOffer.uuid
      history.push(`${locationPathname}?${qs.stringify(query)}`)

      dispatch({
        type: LOAD_BOARD_ACTIONS.OPEN_BID_DIALOG,
        payload: {
          offerUUID: loadOffer.uuid,
          price: loadOffer.price,
          load: loadOffer.load,
        },
      })

      analytics.tag(['Opened Bid Dialog'])
      analytics.track(['Load Offer', 'Bid Button'], {
        position: loadOffer.position,
        triggerContext: context,
        offerType,
        loadUUID,
      })

      storage.set(DISMISS_BID_TOOLTIP_KEY, true)
    },
    [
      query,
      loadOffer.uuid,
      loadOffer.position,
      loadOffer.price,
      loadOffer.load,
      history,
      locationPathname,
      dispatch,
      loadUUID,
      context,
      analytics,
      offerType,
      storage,
    ]
  )

  const handleCardRedirect = useCallback(
    event => {
      tagCardClickAnalytics()
      return onCardRedirect(event)
    },
    [tagCardClickAnalytics, onCardRedirect]
  )

  function handleRejectLoad(event) {
    event.preventDefault()
    event.stopPropagation()
    setIsRejectDialogOpen(true)
  }

  function handleCloseRejectLoadModal() {
    setIsRejectDialogOpen(false)
  }

  const primaryActionButtonText = getButtonLabel(awardedBid)

  const primaryActionButtonProps = {
    text: primaryActionButtonText,
    children: isBooking ? (
      <LoadingV2 width="15px" height="15px" data-testid="offer-loading" />
    ) : (
      primaryActionButtonText
    ),
  }

  const { offered_by_name, pricing_method } = loadOffer
  const isShoot2Carrier = pricing_method === 'manual'
  const offeredBy = !offered_by_name && isShoot2Carrier ? 'Loadsmart' : offered_by_name

  const extra = {
    activeBid,
    analyticsContext: analytics.prefix,
    awardedBid,
    countdown,
    showBidCountdown,
    handleBid,
    handleBook,
    handleRejectLoad,
    hasActiveDNU,
    isBidDisabled,
    isCarrierReady,
    offeredBy,
    primaryActionButtonProps,
    requirements,
    showOnboardingImprovements,
    showPTL,
    displayTooltip,
  }

  const offerCardComponent =
    windowWidth < 1024 || props.forceMobile ? (
      <OfferCardMobile {...props} {...extra} isPublicRelatedLoad={isPublicRelatedLoad} />
    ) : (
      <OfferCardDesktop {...props} {...extra} />
    )

  return (
    <Wrapper data-test-id="OfferCard" className={props.className}>
      {isRejectDialogOpen && (
        <RejectLoadModal onClose={handleCloseRejectLoadModal} load={load} isOpen />
      )}
      {isBookDialogRelatedLoad ? (
        <UnstyledAnchor
          href={`#${urlToLoad}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={tagCardClickAnalytics}
        >
          {offerCardComponent}
        </UnstyledAnchor>
      ) : (
        <UnstyledLink to={urlToLoad} onClick={handleCardRedirect}>
          {offerCardComponent}
        </UnstyledLink>
      )}
    </Wrapper>
  )
}

OfferCard.propTypes = {
  loadOffer: PropTypes.shape({
    load: PropTypes.shape({
      stops: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      mileage: PropTypes.node.isRequired,
      deadhead: PropTypes.string,
      equipment_type: PropTypes.string.isRequired,
      temperature: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      weight: PropTypes.number.isRequired,
      commodity: PropTypes.string,
    }).isRequired,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  context: PropTypes.oneOf(Object.values(CONTEXTS)).isRequired,
  forceMobile: PropTypes.bool,
  onCardRedirect: PropTypes.func,
  showOnlyTwoStops: PropTypes.bool,
  showFeedback: PropTypes.bool,
  showPTL: PropTypes.bool,
}

OfferCard.defaultProps = {
  forceMobile: false,
  onCardRedirect: () => {},
  showOnlyTwoStops: false,
  showFeedback: false,
  showPTL: false,
}

export { OfferCard }
